import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Camera, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import './MobileCameraUploadModal.css';

export type CameraUploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export interface MobileCameraUploadModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for photo upload - receives file and optional comment */
  onUpload: (file: File, comment: string) => Promise<void>;
  /** Modal title */
  title?: string;
  /** Camera button text */
  cameraButtonText?: string;
  /** Comment label */
  commentLabel?: string;
  /** Comment placeholder */
  commentPlaceholder?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Upload button text */
  uploadText?: string;
  /** Uploading button text */
  uploadingText?: string;
  /** Success message */
  successMessage?: string;
  /** Error message (override) */
  errorMessage?: string;
  /** Close immediately after successful upload without showing confirmation */
  closeOnSuccess?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * MobileCameraUploadModal - A mobile-optimized modal for capturing and uploading photos
 * Uses the same design as FileUploadModal but with a camera capture button instead of dropzone
 */
export const MobileCameraUploadModal: React.FC<MobileCameraUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title = 'Upload Photo',
  cameraButtonText = 'Take Photo',
  commentLabel = 'Comment (optional)',
  commentPlaceholder = 'Add a comment...',
  cancelText = 'Cancel',
  uploadText = 'Upload',
  uploadingText = 'Uploading...',
  successMessage = 'Photo uploaded successfully!',
  errorMessage,
  closeOnSuccess = false,
  className = '',
}) => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<CameraUploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setImageFile(null);
    setComment('');
    setError(null);
    setStatus('idle');
    setUploadProgress(0);
  }, [capturedImage]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clean up previous image URL
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
      // Create preview URL
      const imageUrl = URL.createObjectURL(file);
      setCapturedImage(imageUrl);
      setImageFile(file);
      setError(null);
      setStatus('idle');
    }
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
    }
    setCapturedImage(null);
    setImageFile(null);
    setError(null);
    setStatus('idle');
    setUploadProgress(0);
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!imageFile) {
      setError('Please take a photo first');
      return;
    }

    try {
      setStatus('uploading');
      setError(null);

      // Simulate progress for visual feedback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      await onUpload(imageFile, comment);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (closeOnSuccess) {
        handleClose();
      } else {
        setStatus('success');
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setStatus('error');
      setUploadProgress(0);
      setError(errorMessage || (err instanceof Error ? err.message : 'Upload failed'));
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, [capturedImage]);

  if (!isOpen) return null;

  const isUploading = status === 'uploading';
  const isSuccess = status === 'success';

  return (
    <div className="mobile-camera-modal-overlay" onClick={handleClose}>
      <div
        className={`mobile-camera-modal ${className}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-camera-modal-title"
      >
        {/* Header */}
        <div className="mobile-camera-modal-header">
          <h2 id="mobile-camera-modal-title" className="mobile-camera-modal-title">
            {title}
          </h2>
          <button
            className="mobile-camera-modal-close"
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="mobile-camera-modal-body">
          {/* Camera Capture Area */}
          <div
            className={`mobile-camera-capture-area ${capturedImage ? 'mobile-camera-capture-area--has-image' : ''} ${isSuccess ? 'mobile-camera-capture-area--success' : ''}`}
          >
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleCameraCapture}
              accept="image/*"
              capture="environment"
              hidden
              disabled={isUploading}
            />

            {isSuccess ? (
              <div className="mobile-camera-success">
                <CheckCircle className="mobile-camera-success-icon" size={48} />
                <p className="mobile-camera-success-text">{successMessage}</p>
              </div>
            ) : capturedImage && capturedImage.startsWith('blob:') ? (
              <div className="mobile-camera-preview">
                {/* Safe: capturedImage is always a blob URL from URL.createObjectURL() */}
                {/* lgtm[js/xss-through-dom] */}
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="mobile-camera-preview-image"
                />
                {!isUploading && (
                  <button
                    type="button"
                    className="mobile-camera-preview-remove"
                    onClick={handleRemoveImage}
                    aria-label="Remove photo"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="mobile-camera-button"
                onClick={handleCameraClick}
                disabled={isUploading}
              >
                <Camera className="mobile-camera-button-icon" size={48} />
                <span className="mobile-camera-button-text">{cameraButtonText}</span>
              </button>
            )}

            {/* Progress Bar */}
            {isUploading && (
              <div className="mobile-camera-progress">
                <div
                  className="mobile-camera-progress-bar"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>

          {/* Comment Field */}
          <div className="mobile-camera-form-group">
            <label htmlFor="mobile-camera-comment" className="mobile-camera-label">
              {commentLabel}
            </label>
            <textarea
              id="mobile-camera-comment"
              className="mobile-camera-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={commentPlaceholder}
              rows={3}
              disabled={isUploading || isSuccess}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mobile-camera-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="mobile-camera-modal-actions">
            <button
              type="submit"
              className="mobile-camera-btn mobile-camera-btn--primary"
              disabled={!imageFile || isUploading || isSuccess}
            >
              {isUploading ? uploadingText : `${uploadText} (${imageFile ? 1 : 0})`}
            </button>
            <button
              type="button"
              className="mobile-camera-btn mobile-camera-btn--secondary"
              onClick={handleClose}
              disabled={isUploading}
            >
              {cancelText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MobileCameraUploadModal;
